import {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import BrickGreenIcon from "assets/brickGreen.svg";
import BrickGreyIcon from "assets/brickGrey.svg";
import "material-symbols/outlined.css";
import { calculateEstimate } from "lib/calculateEstimate";
import { useTranslation } from "react-i18next";
import AuthContext from "contexts/AuthContext";
import MyTradeInsModal from "components/my-trade-ins-modal";
import QRCode from "react-qr-code";
import InputValidation from "components/input-validation";
import PayallButton from "components/payall-button";
import TangoButton from "components/tango-button";
// import LoadingSpinner from "components/loading-spinner";
import PaymentConfirmation from "components/payment-confirmation";
import GiftcardModal from "components/giftcard-modal";
// import MessageBanner from "components/message-banner";
// import { Button } from "components/button";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Reschedule from "components/appointments/rescheduler";
import stores from "consts/stores.json";
import { Button } from "components/button";
import { AnimatePresence, motion } from "framer-motion";
import DonationWeightChart from "components/donation-weight-chart";
import WeightModal from "components/weight-modal";
import { getMyAppointments } from "components/appointments/service";
import EditDonationModal from "components/edit-donation-modal";
import ShippingModal from "components/shipping-modal";
import ShippingLabelModal from "components/shipping-label-modal";
import DetailedBreakdownModal from "components/detailed-breakdown-modal";
// import { useRetrieveLabelTracking } from "api/use-shipping";
import Tooltip from "components/tooltip";
import { Link } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);

const { format, parse } = require("date-fns");

const { REACT_APP_API } = process.env;

const extractStateFromReturnType = (returnType) => {
  if (!returnType) return null;
  const match = returnType.match(/in_store_(\w{2})/i);
  return match ? match[1].toUpperCase() : null;
};

const getTimezoneForState = (returnType) => {
  const state = extractStateFromReturnType(returnType);
  const timezoneMap = {
    CO: "America/Denver", // MDT
    AR: "America/Chicago", // CDT
    NJ: "America/New_York", // EDT
    PA: "America/New_York", // EDT
  };
  return timezoneMap[state] || dayjs.tz.guess();
};

const getTimezoneAbbreviationForState = (returnType) => {
  const state = extractStateFromReturnType(returnType);
  switch (state) {
    case "CO":
      return "MDT";
    case "AR":
      return "CDT";
    case "NJ":
    case "PA":
      return "EDT";
    default:
      return "";
  }
};

const fetchTakeback = async ({
  accessToken,
  id,
  setTakeback,
  setLoading,
  previousShippingData = null,
}) => {
  setLoading(true);
  try {
    // Fetch takeback first (no polling)
    const takebackRes = await fetch(`${REACT_APP_API}/takeback/${id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!takebackRes.ok) throw new Error("Failed to fetch takeback");
    const takebackData = await takebackRes.json();
    let takebackObj = takebackData?.[0];

    // If mail return type, fetch shipping data
    if (takebackObj?.u_lego_return_type === "mail") {
      // If polling requested, poll shipping endpoint until data changes or max attempts
      if (previousShippingData) {
        let attempts = 0;
        const maxAttempts = 10;
        const pollDelay = 2000;
        let shippingData = null;
        let newShippings = null;
        while (attempts < maxAttempts) {
          const shippingRes = await fetch(
            `${REACT_APP_API}/shipping/retrieve-label-tracking`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ productId: id }),
            }
          );
          if (!shippingRes.ok)
            throw new Error("Failed to fetch shipping label tracking");
          shippingData = await shippingRes.json();
          newShippings =
            Array.isArray(shippingData?.data) && shippingData.data.length > 0
              ? shippingData.data
              : [];
          if (
            JSON.stringify(newShippings) !==
            JSON.stringify(previousShippingData)
          ) {
            break;
          }
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise((res) => setTimeout(res, pollDelay));
          }
        }
        takebackObj = { ...takebackObj, shippings: newShippings };
      } else {
        // No polling, just fetch once
        const shippingRes = await fetch(
          `${REACT_APP_API}/shipping/retrieve-label-tracking`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ productId: id }),
          }
        );
        if (!shippingRes.ok)
          throw new Error("Failed to fetch shipping label tracking");
        const shippingData = await shippingRes.json();
        if (Array.isArray(shippingData?.data) && shippingData.data.length > 0) {
          takebackObj = { ...takebackObj, shippings: shippingData.data };
        }
      }
    }
    setTakeback(takebackObj);
  } catch (error) {
    console.error(error);
  }
  setLoading(false);
};

const formatDate = ({ utcDateStr, formatStr }) => {
  const inputFormats = [
    "yyyy-MM-dd HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm:ss.SSS",
    "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
    "yyyy-MM-dd",
    "MM/dd/yyyy",
    "dd/MM/yyyy",
    "MM-dd-yyyy",
    "dd-MM-yyyy",
  ];

  let parsedDate = null;

  // Check if input is a unix timestamp (seconds or milliseconds)
  if (/^\d+$/.test(utcDateStr)) {
    const timestamp = parseInt(utcDateStr);
    // If timestamp is in seconds (< year 2286), convert to milliseconds
    const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    parsedDate = new Date(timestampMs);
  } else {
    // Try to parse with each format until one works
    for (const inputFormat of inputFormats) {
      try {
        parsedDate = parse(utcDateStr, inputFormat, new Date());
        if (!isNaN(parsedDate.getTime())) {
          break;
        }
      } catch (error) {
        // Continue to next format
      }
    }

    // If no format worked, try native Date parsing as fallback
    if (!parsedDate || isNaN(parsedDate.getTime())) {
      parsedDate = new Date(utcDateStr);
      if (isNaN(parsedDate.getTime())) {
        // Return original string if all parsing attempts fail
        return utcDateStr;
      }
    }
  }

  const utcDate = new Date(
    Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      parsedDate.getHours(),
      parsedDate.getMinutes(),
      parsedDate.getSeconds()
    )
  );

  const localDate = new Date(utcDate);

  return format(localDate, formatStr);
};

// Add a helper for formatting the appointment time range
const formatAppointmentTimeRange = (startISO, duration, returnType) => {
  if (!startISO) return null;
  const timezone = getTimezoneForState(returnType);
  const tzAbbr = getTimezoneAbbreviationForState(returnType);
  const startObj = dayjs(startISO).tz(timezone);
  const durationMinutes = duration || 30;
  const endObj = startObj.add(durationMinutes, "minute");
  return (
    startObj.format("dddd, MMMM D") +
    " at " +
    startObj.format("h:mm A") +
    " - " +
    endObj.format("h:mm A") +
    (tzAbbr ? ` ${tzAbbr}` : "")
  );
};

export default function MyTradeInsCard({
  id,
  setPayallOpen,
  setReadyCount,
  payallCompleted,
  index,
}) {
  const [takeback, setTakeback] = useState();
  const { t } = useTranslation();
  const { user, authTokens } = useContext(AuthContext);
  const qrCodeValue = id;
  const [modalOpen, setModalOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [giftcardModalOpen, setGiftcardModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState();
  const [weightModalOpen, setWeightModalOpen] = useState(false);
  const [donationModalOpen, setDonationModalOpen] = useState(false);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [shippingLabelModalOpen, setShippingLabelModalOpen] = useState(false);
  const [shippingPresent, setShippingPresent] = useState(false);
  const [detailedBreakdownModalOpen, setDetailedBreakdownModalOpen] =
    useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  const toggleMenu = useCallback(() => setMenuOpen(!menuOpen), [menuOpen]);
  const handleOpenModal = useCallback(() => {
    setMenuOpen(false);
    openModal();
  }, [setMenuOpen, openModal]);

  const handleReceiptOpen = useCallback(() => {
    setMenuOpen(false);
    const receiptUrl = `${window.location.origin}/api/pdf/receipt?caseId=${takeback?.number}&token=${authTokens.access_token}`;
    window.open(receiptUrl, "_blank");
  }, [setMenuOpen, takeback?.number, authTokens.access_token]);

  const menuButtonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    // Clear appointment from localStorage on the mytrade ins page
    // This is to prevent the appointment from being used on future trade ins.
    localStorage.removeItem("appointment");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        menuButtonRef.current &&
        menuRef.current &&
        !menuButtonRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpen]);

  const [pdfUrl, setPdfUrl] = useState();
  const issuedAt = takeback?.rawTakeback?.takeback_processing_history_list.find(
    ({ status }) => status === "payment-create-SUCCEEDED"
  )?.updated_at;
  const consumerAddressCountry = takeback?.consumer?.country;
  const shippingLabel =
    takeback?.u_lego_dhl_label_b64_encoded ||
    takeback?.u_ups_shipping_label_encoded;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyAppointments(user.email).then((appointments) => {
      setAppointments(appointments);
    });
  }, [user.email]);

  useEffect(() => {
    fetchTakeback({
      accessToken: authTokens.access_token,
      id,
      setTakeback,
      setLoading,
    });
  }, [id, authTokens.access_token]);

  useEffect(() => {
    if (shippingLabel) {
      const base64Data = shippingLabel;
      const blob = new Blob(
        [Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))],
        { type: "application/pdf" }
      );
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      return () => URL.revokeObjectURL(url);
    }
  }, [shippingLabel]);

  // const currencySymbol = consumerAddressCountry === "DE" ? "€" : "$";
  const takeBackCountry = consumerAddressCountry === "DE" ? "de-DE" : "en-US";
  const unit = consumerAddressCountry === "DE" ? "kg" : "lbs";

  //temporary loading state for card
  useEffect(() => {
    if (!loading) {
      setReadyCount((prevState) => prevState + 1);
    }
  }, [loading, setReadyCount]);

  //Payall modal was just completed. This state is used to update the card without needing a page reload / re-fetch
  const justCompleted = Boolean(payallCompleted === takeback?.number);
  const returnMethod = takeback?.u_lego_return_type;

  // const snowPayallCompleted =
  //   takeback.u_lego_payall_payment_status === "Completed";
  const payallPending =
    takeback?.u_lego_payment_provider === `payall` &&
    takeback?.u_lego_payall_payment_status !== `Completed`;

  const bodyModal = useMemo(
    () =>
      takeback?.u_lego_return_type === "mail" ? (
        shippingLabel ? (
          <iframe
            src={pdfUrl}
            title={t("my_trade_ins.label_header")}
            className="min-w-full min-h-full"
          />
        ) : (
          <InputValidation
            errorMessage={t("trade-in.next_steps.print_warning")}
            fontSize="text-base"
            className="ml-8"
          />
        )
      ) : (
        <QRCode className="my-auto" value={qrCodeValue} size={250} />
      ),
    [takeback?.u_lego_return_type, shippingLabel, pdfUrl, t, qrCodeValue]
  );

  const verifiedAt =
    takeback?.rawTakeback.takeback_processing_history_list.find(
      ({ parent_status }) => parent_status === "processed"
    )?.updated_at;

  const payoutMethod = takeback?.u_lego_payment_type;

  // Get checked-in and associate verified dates
  const checkedInDate = useMemo(
    () =>
      takeback?.status_history?.find(
        ({ status }) => status === "in-store-checkin"
      )?.updated_at,
    [takeback?.status_history]
  );

  const associateVerifiedDate = useMemo(
    () =>
      takeback?.status_history?.find(
        ({ status }) => status === "associate-verified"
      )?.updated_at,
    [takeback?.status_history]
  );

  const stepsMail = useMemo(
    () => [
      {
        label: t("my_trade_ins.trade_in_created"),
        date: takeback?.sys_created_on,
      },
      {
        label: t("my_trade_ins.package_sent"),
        date: takeback?.u_lego_sender_shipped_at,
      },
      { label: t("my_trade_ins.arrived"), date: takeback?.u_lego_received_at },
      {
        label: t("my_trade_ins.verified"),
        date: verifiedAt,
        value: "verified",
      },
      {
        label:
          payoutMethod === "giftcard"
            ? t("my_trade_ins.giftcard_issued")
            : t("my_trade_ins.digital_cash_ready"),
        date: issuedAt,
        value: "paid",
        ignoreDate: payallPending,
      },
    ],
    [
      takeback?.sys_created_on,
      takeback?.u_lego_sender_shipped_at,
      takeback?.u_lego_received_at,
      verifiedAt,
      payoutMethod,
      payallPending,
      t,
      issuedAt,
    ]
  );

  const stepsInStore = useMemo(
    () => [
      {
        label: t("my_trade_ins.trade_in_created"),
        date: takeback?.sys_created_on,
      },
      {
        label: "In-Store Return",
        date: checkedInDate,
      },
      {
        label: "Bricks Reviewed",
        date: associateVerifiedDate,
      },
      {
        label:
          payoutMethod === "giftcard"
            ? t("my_trade_ins.giftcard_issued")
            : t("my_trade_ins.digital_cash_ready"),
        date: issuedAt,
        value: "paid",
      },
    ],
    [
      takeback?.sys_created_on,
      checkedInDate,
      associateVerifiedDate,
      payoutMethod,
      t,
      issuedAt,
    ]
  );

  const steps = useMemo(
    () => (returnMethod === "mail" ? stepsMail : stepsInStore),
    [returnMethod, stepsMail, stepsInStore]
  );

  const activeIndex = useMemo(
    () =>
      steps.reduce((latestIndex, step, index) => {
        if (
          payoutMethod === "giftcard" &&
          takeback?.u_lego_diggecard_card_number &&
          index === steps.length - 1
        ) {
          return index;
        }
        if (!step.date || (step.date && step.ignoreDate)) return latestIndex;
        return index;
      }, -1),
    [steps, payoutMethod, takeback?.u_lego_diggecard_card_number]
  );

  //weights
  const verifiedReplayWeight = useMemo(
    () => takeback?.verifiedReplayWeight || 0,
    [takeback?.verifiedReplayWeight]
  );
  const originalReplayWeight = useMemo(
    () => takeback?.originalReplayWeight,
    [takeback?.originalReplayWeight]
  );

  const replayWeight = verifiedReplayWeight || originalReplayWeight;
  const presortWeight = takeback?.rawTakeback?.data.originalEstimatedWeight;
  const replayDonation = takeback?.replayPercentage;

  const paymentWeight =
    takeback?.u_lego_payment_weight ||
    takeback?.rawTakeback?.data?.payment_weight ||
    0;
  const postsortWeight = useMemo(
    () => takeback?.u_lego_postsort_weight || 0,
    [takeback?.u_lego_postsort_weight]
  );
  const receivedWeight = useMemo(
    () => takeback?.receivedWeight || 0,
    [takeback?.receivedWeight]
  );
  const replayPercentage = useMemo(
    () => takeback?.replayPercentage || null,
    [takeback?.replayPercentage]
  );

  const displayWeight = useMemo(
    () => parseFloat(postsortWeight || presortWeight).toFixed(2),
    [postsortWeight, presortWeight]
  );

  const currentWeight = useMemo(
    () =>
      verifiedAt
        ? parseFloat(postsortWeight).toFixed(2)
        : parseFloat(presortWeight).toFixed(2),
    [verifiedAt, postsortWeight, presortWeight]
  );

  // const nonConformWeight = verifiedAt
  //   ? Math.max(0, presortWeight - postsortWeight)
  //   : 0;
  const returnTypeOffered = takeback?.rawTakeback?.data?.returnTypeOffered;

  //values
  const presortValueWeight = useMemo(
    () =>
      originalReplayWeight
        ? presortWeight - originalReplayWeight
        : presortWeight,
    [originalReplayWeight, presortWeight]
  );

  const currentValue = useMemo(
    () =>
      calculateEstimate(
        verifiedAt ? paymentWeight : presortValueWeight,
        takeback?.u_lego_incentive_ratio
      ).gift,
    [
      verifiedAt,
      paymentWeight,
      presortValueWeight,
      takeback?.u_lego_incentive_ratio,
    ]
  );

  const takebackState = takeback?.u_lego_return_type.startsWith("in_store")
    ? takeback?.u_lego_return_type?.split("_").slice(-1)[0].toUpperCase()
    : null;

  const presortValue = useMemo(
    () =>
      calculateEstimate(presortValueWeight, takeback?.u_lego_incentive_ratio)
        .gift,
    [presortValueWeight, takeback?.u_lego_incentive_ratio]
  );

  const paymentValue = useMemo(
    () =>
      calculateEstimate(paymentWeight, takeback?.u_lego_incentive_ratio).gift,
    [paymentWeight, takeback?.u_lego_incentive_ratio]
  );

  const dotRefs = useRef([]);
  const [lineHeight, setLineHeight] = useState(0);

  useEffect(() => {
    if (dotRefs.current[activeIndex]) {
      const activeDotTop = dotRefs.current[activeIndex].offsetTop;
      setLineHeight(activeDotTop);
    }
  }, [activeIndex, steps.length]);

  const showPayment = false;

  //payment confirmation logic
  const ibanIsSet = Boolean(takeback?.u_lego_seller_iban) || justCompleted;

  const payallHeading = issuedAt
    ? t("my_trade_ins.payall_issued_heading")
    : t("my_trade_ins.payall_details_accepted_heading");
  const payallMessage = issuedAt
    ? t("my_trade_ins.payall_issued_message")
    : t("my_trade_ins.payall_details_accepted_message");

  //shipping data
  // const { data: labelTracking } = useRetrieveLabelTracking(
  //   { productId: takeback?.sys_id }
  //    {
  //      refetchInterval: false,
  //      retry: 8,
  //      retryDelay: 5000,
  //    }
  // );
  // useEffect(() => {
  //   if (labelTracking && labelTracking?.data?.length === 0) {
  //     const interval = setInterval(() => {
  //       refetch();
  //     }, 10000);
  //     return () => clearInterval(interval);
  //   }
  // }, [labelTracking, refetch]);

  useEffect(() => {
    const shippingPresent =
      Array.isArray(takeback?.shippings) && takeback.shippings.length > 0;
    setShippingPresent(shippingPresent);
  }, [takeback?.shippings]);
  const shippingType = takeback?.shippings?.[0]?.type;
  const originalLabelUrl =
    takeback?.shippings?.[0]?.shipping_metadatas?.items?.find(
      (item) => item.key === "label_url"
    )?.value;

  // Extract returnID from original URL and construct new URL with caseId
  const labelUrl =
    originalLabelUrl && takeback?.number
      ? (() => {
          const url = new URL(originalLabelUrl);
          const returnID = url.searchParams.get("returnID");
          if (returnID) {
            return `${window.location.origin}/api/pdf/shipping-label?returnID=${returnID}&caseId=${takeback.number}&weight=${displayWeight}&token=${authTokens.access_token}`;
          }
          return originalLabelUrl; // Fallback to original if can't extract returnID
        })()
      : originalLabelUrl;

  const appointment = appointments.filter(
    (appointment) => appointment.sys_id === takeback?.sys_id
  )[0];

  // const location = useLocation();
  // Extract only the 'state' param from the current query string
  // const params = new URLSearchParams(location.search);
  // const stateParam = params.get("state");
  // const stateQuery = stateParam
  //   ? `?state=${encodeURIComponent(stateParam)}`
  //   : "";

  const handleTermsExternal = () => {
    window.open(
      `/en-US/dropoff-seller/confirm-details?id=${takeback?.number}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div
      className={`bg-white p-6 md:p-8 rounded-lg mx-auto mt-10 border border-gray-300 w-full relative ${!takeback?.number ? `hidden` : ``}`}
    >
      <AnimatePresence>
        <div className="flex">
          <div
            className={`w-20 pr-8 hidden md:block ${
              takeback?.u_lego_return_type?.includes("in_store") &&
              takeback?.state === "8" &&
              "opacity-30"
            }`}
          >
            {verifiedAt ? (
              <img src={BrickGreenIcon} alt="LEGO Brick Icon" />
            ) : (
              <img src={BrickGreyIcon} alt="LEGO Brick Icon" />
            )}
          </div>
          <div className="flex-1 flex flex-col space-y-3">
            <div
              className={`flex-1 flex flex-col space-y-3 ${
                takeback?.u_lego_return_type?.includes("in_store") &&
                takeback?.state === "8" &&
                "opacity-30"
              }`}
            >
              <div className="flex justify-between items-center -mt-2">
                <div
                  className={`text-sm leading-none uppercase font-semibold ${verifiedAt ? "text-green-600" : "text-black-300"}`}
                >
                  {verifiedAt ? "Confirmed Weight & Value" : "Your Estimate"}
                </div>
                <div className="relative">
                  <button
                    onClick={toggleMenu}
                    className={`material-symbols-outlined text-secondary hover:bg-gray-300 p-2 rounded-full`}
                    ref={menuButtonRef}
                  >
                    more_horiz
                  </button>
                  <AnimatePresence>
                    {menuOpen && (
                      <motion.div
                        className="takeback-menu absolute right-0 mt-2 w-64 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20"
                        ref={menuRef}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <div
                          className="py-2"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="options-menu"
                        >
                          <div className="px-4 py-1 flex flex-col gap-1">
                            <span className="block font-bold text-sm text-black">
                              {t("my_trade_ins.context_title")}
                            </span>
                            <div className="text-black-300 text-sm">
                              {takeback?.number}
                            </div>
                          </div>
                          {takeback.state !== 3 &&
                            (takeback.u_dhl_transit_timestamp ||
                              takeback.u_ups_transit_timestamp) && (
                              <button
                                onClick={handleOpenModal}
                                className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                                role="menuitem"
                              >
                                {returnMethod === "mail"
                                  ? t("my_trade_ins.shipping_label")
                                  : t("my_trade_ins.qr_code")}
                              </button>
                            )}
                          {steps[activeIndex]?.value === "paid" && (
                            <button
                              onClick={handleReceiptOpen}
                              className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                              role="menuitem"
                            >
                              {t("my_trade_ins.receipt")}
                            </button>
                          )}
                          {steps[activeIndex]?.value === "paid" && (
                            <button
                              onClick={() => setGiftcardModalOpen(true)}
                              className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                              role="menuitem"
                            >
                              {t("my_trade_ins.show_giftcard")}
                            </button>
                          )}
                          {verifiedAt && (
                            <button
                              onClick={() => {
                                setMenuOpen(false);
                                setDetailedBreakdownModalOpen(true);
                              }}
                              className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                              role="menuitem"
                            >
                              Show detailed breakdown
                            </button>
                          )}
                          {takeback?.u_lego_return_type?.includes("in_store") &&
                            !verifiedAt && (
                              <button
                                onClick={handleOpenModal}
                                className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                                role="menuitem"
                              >
                                View Check-In QR Code
                              </button>
                            )}
                          {takeback?.u_lego_return_type?.includes("mail") &&
                            activeIndex === 0 && (
                              <button
                                className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                                role="menuitem"
                                onClick={() => {
                                  if (
                                    shippingPresent &&
                                    shippingType !== "mail-nolabel"
                                  ) {
                                    if (labelUrl) {
                                      window.open(labelUrl, "_blank");
                                      return;
                                    }
                                  }
                                  shippingPresent
                                    ? setShippingLabelModalOpen(true)
                                    : setShippingModalOpen(true);
                                }}
                              >
                                {shippingPresent
                                  ? shippingType === "mail-nolabel"
                                    ? "View UPS QR Code"
                                    : "View UPS Shipping Label"
                                  : "Get UPS Shipping Label or UPS QR Code"}
                              </button>
                            )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <h2 className="text-4xl font-semibold leading-none">
                {displayWeight} {unit} {t("my_trade_ins.card_subtitle")}
              </h2>
              <div className="text-secondary">
                ${currentValue}{" "}
                {payoutMethod === "giftcard"
                  ? t("my_trade_ins.card_giftcard")
                  : t("my_trade_ins.card_payment")}
              </div>

              {verifiedAt && (
                <div>
                  <div
                    className="text-sm cursor-pointer"
                    onClick={() => setShowEstimate(!showEstimate)}
                  >
                    <span className="text-black-300 select-none">
                      {showEstimate ? "Hide" : "Show"} your estimate
                    </span>
                    <span
                      className={`material-symbols-outlined ml-1 translate-y-[7px] select-none ${
                        showEstimate ? "rotate-180" : ""
                      }`}
                    >
                      keyboard_arrow_down
                    </span>
                  </div>
                  <AnimatePresence>
                    {showEstimate && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div>
                          <span className="text-black-300 uppercase font-bold text-sm">
                            Your original estimate
                          </span>
                          <Tooltip
                            content={
                              <span>
                                Weight discrepancies occur if the initial
                                estimate was inaccurate or ineligible LEGO
                                bricks were included in your trade-in.{" "}
                                <Link
                                  to={`/en-US/preparation-guidelines?state=${takebackState}&preselected=in-store`}
                                  className="text-blue-600 hover:text-blue-800"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Click here to view our acceptance criteria.
                                </Link>
                              </span>
                            }
                          >
                            <span
                              className="material-symbols-outlined ml-1 translate-y-[6px] scale-75 text-gray-400 select-none"
                              tabIndex={0}
                            >
                              info
                            </span>
                          </Tooltip>
                        </div>
                        <div className="font-bold text-base">
                          {parseFloat(presortWeight).toFixed(2)} lbs of LEGO
                          bricks
                        </div>
                        <div className="text-base pb-4">
                          ${presortValue}{" "}
                          {originalReplayWeight
                            ? `(${presortValueWeight.toFixed(2)} lbs)`
                            : ""}{" "}
                          LEGO e-Gift Card
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {replayWeight !== undefined && replayWeight > 0 && (
                <div>
                  <div className="w-full flex justify-between my-2">
                    <div className=" text-secondary">
                      Your trade-in breakdown
                    </div>
                    {verifiedAt && (
                      <div
                        className="text-blue cursor-pointer hover:underline"
                        onClick={() => {
                          setDetailedBreakdownModalOpen(true);
                        }}
                      >
                        Show detailed breakdown
                      </div>
                    )}
                  </div>
                  <DonationWeightChart
                    totalWeight={
                      paymentWeight
                        ? parseFloat(replayWeight) + parseFloat(paymentWeight)
                        : parseFloat(presortWeight)
                    }
                    replayWeight={parseFloat(replayWeight)}
                    className="my-2"
                    rewardCaption={verifiedAt ? "Reward" : "Estimated reward"}
                    replayPercentage={parseFloat(replayPercentage)}
                  />
                </div>
              )}

              {takeback?.u_lego_return_type?.includes("in_store") &&
                !returnTypeOffered?.includes("Quick Reg") &&
                activeIndex === 0 && (
                  <>
                    <div className="text-sm flex pt-2 pb-3">
                      <div className="pr-2">
                        <span
                          className={`material-symbols-outlined  text-black`}
                        >
                          event
                        </span>
                      </div>

                      <div className="flex flex-col gap-1 md:gap-0">
                        {stores.some(
                          (store) =>
                            takeback?.u_lego_return_type?.includes(
                              store.value
                            ) && store.return_type === "kiosk"
                        )
                          ? (() => {
                              // Show appointment as a time range
                              const appt = appointment;
                              const startISO = appt?.time;
                              let duration = appt?.duration;
                              if (!duration) duration = 30;
                              return startISO
                                ? `Your appointment: ${formatAppointmentTimeRange(
                                    startISO,
                                    duration,
                                    takeback?.u_lego_return_type
                                  )}`
                                : "Your appointment: (not scheduled)";
                            })()
                          : "Pop by our store at a time and date that works for you!"}
                        <br />
                        <div className="text-black-300">
                          {t("trade-in.summary.location")}:{" "}
                          {stores.find(
                            (store) =>
                              store.value === takeback?.u_lego_return_type
                          )?.address || takeback?.u_lego_in_store_name}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              <div className="pb-2 flex space-y-2 md:space-y-0 md:space-x-2 flex-col md:flex-row">
                {activeIndex === 0 && (
                  <Button
                    onClick={() => setWeightModalOpen(true)}
                    icon="edit"
                    iconPosition="left"
                    variant="tertiary"
                    size="small"
                  >
                    Edit Weight
                  </Button>
                )}

                {activeIndex === 0 &&
                  takeback?.u_lego_return_type === "mail" && (
                    <Button
                      onClick={() => setDonationModalOpen(true)}
                      icon={
                        <svg
                          width="19"
                          height="14"
                          viewBox="0 0 19 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 mx-1"
                        >
                          <path
                            d="M7 2H8.5V4H10.5V2H12V0H15V2H16.5V4H18.5V6H16.5V8H14.5V10H12.5V12H10.5V14H8.5V12H6.5V10H4.5V8H2.5V6H0.5V4H2.5V2H4V0H7V2Z"
                            fill="#757575"
                          />
                        </svg>
                      }
                      iconPosition="left"
                      variant="tertiary"
                      size="small"
                    >
                      {replayWeight ? "Edit" : "Add"} Donation
                    </Button>
                  )}

                {stores.some(
                  (store) =>
                    takeback?.u_lego_return_type?.includes(store.value) &&
                    store.return_type === "kiosk" &&
                    appointment &&
                    appointment.id
                ) &&
                  activeIndex === 0 &&
                  !returnTypeOffered.includes("Quick Reg") && (
                    <Button
                      onClick={() => {
                        setSelectedAppointment({
                          id: appointment.id,
                          takebackId: takeback?.number,
                        });
                      }}
                      icon="edit_calendar"
                      iconPosition="left"
                      variant="tertiary"
                      size="small"
                    >
                      Edit Appointment
                    </Button>
                  )}
              </div>
              {/* mobile timeline */}
              <div className="lg:hidden relative w-full flex flex-col gap-8 my-4">
                <div
                  className={`absolute left-2 top-2 ${
                    activeIndex === steps.length - 1 ? "bottom-6" : "bottom-2"
                  } w-1 bg-gray-300`}
                ></div>

                <div
                  className="absolute left-2 w-1 bg-green"
                  style={{
                    top: "1rem",
                    height: `${lineHeight}px`,
                    transition: "height 0.3s ease",
                  }}
                ></div>

                {steps.map((step, index) => (
                  <div
                    className="flex"
                    key={index}
                    ref={(el) => (dotRefs.current[index] = el)}
                  >
                    <div
                      className={`mt-1 w-5 h-5 rounded-full border-2 border-white z-10 ${
                        index <= activeIndex ? "bg-green" : "bg-gray-300"
                      }`}
                    ></div>
                    <div className={`ml-4 mt-1 text-sm`}>
                      <div
                        className={`${index === activeIndex ? "font-bold" : ""}`}
                      >
                        {step.label}
                      </div>
                      {step.date && (
                        <div className="text-tertiary">
                          {formatDate({
                            utcDateStr: step.date,
                            formatStr: "MM/dd/yyyy",
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* desktop timeline */}
              <div className="hidden lg:flex relative w-full items-start justify-between pb-6">
                <div className="absolute left-0 right-0 top-2 h-1 bg-gray-300"></div>
                <div
                  className="absolute left-0 h-1 top-2 bg-green"
                  style={{
                    width: `${(activeIndex / (steps.length - 1)) * 100}%`,
                    transition: "width 0.3s ease",
                  }}
                ></div>
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="relative flex flex-col items-center h-14"
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 border-white z-10 ${
                        index <= activeIndex ? "bg-green" : "bg-gray-300"
                      }`}
                    ></div>
                    <div
                      className={`absolute top-6 text-sm flex flex-col  ${
                        index === 0
                          ? "text-left left-0 items-start"
                          : index === steps.length - 1
                            ? "text-right right-0 items-end"
                            : "text-center items-center"
                      }`}
                    >
                      <div
                        className={`min-w-[160px] ${
                          index === activeIndex ? "font-bold" : ""
                        }`}
                      >
                        {step.label}
                      </div>
                      {step.date && (
                        <div className="text-tertiary">
                          {formatDate({
                            utcDateStr: step.date,
                            formatStr: "MM/dd/yyyy",
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* UPS tracking link */}
              {shippingPresent && !verifiedAt && (
                <div className="pb-2 pt-2 flex items-center">
                  <a
                    href={`https://www.ups.com/track?loc=en_US&tracknum=${takeback?.shippings?.[0]?.tracking_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 group flex items-center gap-1"
                  >
                    <span className="group-hover:underline">
                      View tracking on UPS
                    </span>
                    <span className="material-symbols-outlined scale-75">
                      open_in_new
                    </span>
                  </a>
                </div>
              )}
              {takeback?.u_lego_return_type?.includes("in_store") &&
                activeIndex === 0 && (
                  <Button className="w-full" onClick={handleOpenModal}>
                    View Check-In QR Code
                  </Button>
                )}
              {takeback?.u_lego_return_type?.includes("in_store") &&
                checkedInDate &&
                !verifiedAt && (
                  <Button className="w-full" onClick={handleTermsExternal}>
                    <span className="truncate">
                      Review & Accept Terms and Conditions
                    </span>
                  </Button>
                )}
              {takeback?.u_lego_return_type?.includes("mail") &&
                activeIndex === 0 && (
                  <Button
                    className="w-full"
                    data-testid={`ups-shipping-label-${takeback?.number}`}
                    onClick={() => {
                      if (shippingPresent && shippingType !== "mail-nolabel") {
                        // Extract label_url from shipping_metadatas.items

                        if (labelUrl) {
                          window.open(labelUrl, "_blank");
                          return;
                        }
                      }
                      // Fallback to modal logic
                      shippingPresent
                        ? setShippingLabelModalOpen(true)
                        : setShippingModalOpen(true);
                    }}
                  >
                    {shippingPresent
                      ? shippingType === "mail-nolabel"
                        ? "View UPS QR Code"
                        : "View UPS Shipping Label"
                      : "Get UPS Shipping Label or UPS QR Code"}
                  </Button>
                )}
              {steps[activeIndex]?.value === "paid" && (
                <Button
                  className="w-full"
                  onClick={() => setGiftcardModalOpen(true)}
                >
                  View e-Gift Card
                </Button>
              )}
              {showPayment &&
                payoutMethod !== "giftcard" &&
                (steps[activeIndex]?.value === "verified" ||
                  steps[activeIndex]?.value === "paid") &&
                takeBackCountry === "de-DE" &&
                !ibanIsSet &&
                !issuedAt && (
                  <PayallButton
                    className="mt-6 w-full"
                    setModalOpen={setPayallOpen}
                  />
                )}
              {showPayment &&
                payoutMethod !== "giftcard" &&
                steps[activeIndex]?.value === "paid" &&
                takeBackCountry === "en-US" &&
                takeback?.u_tango_redeem_link && (
                  <TangoButton
                    caseId={takeback?.number}
                    u_tango_redeem_link={takeback?.u_tango_redeem_link}
                    className="mt-6 w-full"
                    activeStep={steps[activeIndex]?.value}
                  />
                )}
              {/* show payment confirmation once paid or error message on payment error */}
              {!justCompleted &&
                takeBackCountry === "de-DE" &&
                payoutMethod !== "giftcard" &&
                (ibanIsSet || issuedAt) && (
                  <PaymentConfirmation
                    heading={payallHeading}
                    message={payallMessage}
                    // warningState={ibanIsSet && !snowPayallCompleted}
                  />
                )}
            </div>

            {modalOpen && (
              <MyTradeInsModal
                className="z-50"
                isOpen={modalOpen}
                onClose={closeModal}
                onConfirm={closeModal}
                print={returnMethod === "mail"}
                title={
                  returnMethod === "mail"
                    ? t("my_trade_ins.label_header")
                    : t("my_trade_ins.qr_code")
                }
                body={bodyModal}
                primaryText={t("my_trade_ins.print_label")}
                secondaryText={t("my_trade_ins.close_modal")}
                pdfUrl={pdfUrl}
              />
            )}

            <AnimatePresence>
              {giftcardModalOpen && (
                <GiftcardModal
                  setModalOpen={setGiftcardModalOpen}
                  cardNumber={takeback?.u_lego_diggecard_card_number}
                  cardPin={takeback?.u_lego_diggecard_card_pin}
                  takebackId={takeback?.sys_id}
                  title={`$${verifiedAt ? paymentValue : presortValue} ${t("my_trade_ins.card_giftcard")}`}
                />
              )}
            </AnimatePresence>

            {selectedAppointment && (
              <Reschedule
                close={(booking) => {
                  if (booking && booking.time) {
                    setAppointments((prevAppointments) =>
                      prevAppointments.map((app) =>
                        app.id === selectedAppointment.id
                          ? { ...app, time: booking.time }
                          : app
                      )
                    );
                    setSelectedAppointment();
                  } else {
                    setSelectedAppointment();
                  }
                }}
                id={selectedAppointment.id}
                takebackId={selectedAppointment.takebackId}
                weight={parseFloat(presortWeight)}
                showDescription={true}
                location={
                  stores.find(
                    (store) => store.value === takeback?.u_lego_return_type
                  )?.name
                }
              />
            )}

            <AnimatePresence>
              {weightModalOpen && (
                <WeightModal
                  onSuccess={async () => {
                    await fetchTakeback({
                      accessToken: authTokens.access_token,
                      id,
                      setTakeback,
                      setLoading,
                      previousShippingData: takeback?.shippings || [],
                    });
                    setWeightModalOpen(false);
                  }}
                  setModalOpen={setWeightModalOpen}
                  modalOpen={weightModalOpen}
                  currentWeight={currentWeight}
                  replayWeight={parseFloat(replayWeight) || 0}
                  replayDonation={parseFloat(replayDonation) || 0}
                  returnMethod={returnMethod}
                  caseId={takeback?.number}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {donationModalOpen && (
                <EditDonationModal
                  setModalOpen={setDonationModalOpen}
                  modalOpen={donationModalOpen}
                  currentWeight={parseFloat(presortWeight).toFixed(2)}
                  replayWeight={parseFloat(replayWeight).toFixed(2)}
                  caseId={takeback?.number}
                  replayPercentage={replayPercentage}
                  onSuccess={() => {
                    fetchTakeback({
                      accessToken: authTokens.access_token,
                      id,
                      setTakeback,
                      setLoading,
                    });
                  }}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {shippingModalOpen && (
                <ShippingModal
                  setModalOpen={setShippingModalOpen}
                  modalOpen={shippingModalOpen}
                  onConfirm={() => setShippingModalOpen(false)}
                  customerId={takeback?.consumer?.value}
                  takebackId={takeback?.sys_id}
                  weight={displayWeight}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {shippingLabelModalOpen && (
                <ShippingLabelModal
                  shipping={takeback?.shippings?.[0]}
                  setModalOpen={setShippingLabelModalOpen}
                  modalOpen={shippingLabelModalOpen}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {detailedBreakdownModalOpen && (
                <DetailedBreakdownModal
                  takebackMethod={takeback?.u_lego_return_type}
                  setModalOpen={setDetailedBreakdownModalOpen}
                  modalOpen={detailedBreakdownModalOpen}
                  presortWeight={presortWeight}
                  receivedWeight={receivedWeight}
                  postsortWeight={postsortWeight}
                  replayWeight={
                    isNaN(Number(replayWeight)) ? 0 : Number(replayWeight)
                  }
                  paymentWeight={paymentWeight}
                  replayPercentage={
                    isNaN(Number(replayPercentage))
                      ? 0
                      : Number(replayPercentage)
                  }
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </AnimatePresence>
    </div>
  );
}
